import { PolicyInputRow, PolicyRow, ProductRow } from "namirasoft-access";
import { NSARouterMakerResult } from "namirasoft-account-react";
import { NamingConvention } from "namirasoft-core";
import { NSBox, NSBoxBooleans, NSBoxCombo, NSBoxEntity, NSBoxString, NSBoxTextArea, NSColumn, NSLine, NSRepeater, NSRow, NSTitle } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { AccessEntities } from "../AccessEntities";
import { AccessLayout } from "../AccessLayout";
import { AccessProps } from "../AccessProps";
import ResourceLine from "../components/ResourceLine";

export enum PolicyEditPageEffect
{
    Allow = "Allow",
    Deny = "Deny",
}
export interface PolicyEditPageProps extends NSARouterMakerResult, AccessProps
{
    isEdit: boolean
}

interface PolicyEditPageState
{
    product: ProductRow | null;
    entity: string | null;
}

export default class PolicyEditPage extends Component<PolicyEditPageProps, PolicyEditPageState>
{
    private NSBoxString_Name = createRef<NSBoxString>();
    private NSBoxCombo_Effect = createRef<NSBoxCombo>();
    private NSBoxEntity_Product = createRef<NSBoxEntity<ProductRow>>();
    private NSBoxCombo_Entity = createRef<NSBoxCombo>();
    private NSBoxBooleans_Entity = createRef<NSBoxBooleans>();
    private NSRepeater_Resources = createRef<NSRepeater<ResourceLine, string>>();
    private NSBoxTextArea_Description = createRef<NSBoxTextArea>();

    constructor(props: PolicyEditPageProps)
    {
        super(props);
        this.state = {
            product: null,
            entity: ""
        }
        this.onChangeProduct = this.onChangeProduct.bind(this);
        this.onChangeEntity = this.onChangeEntity.bind(this);
    }
    private onChangeProduct(e: NSBoxEntity<ProductRow>, callback?: () => void) 
    {
        let product = e.getDataOne();
        this.setState({ product, entity: null }, () =>
        {
            this.NSBoxCombo_Entity.current?.setValue(null, () =>
            {
                this.NSBoxCombo_Entity.current?.reload();
            })
            callback?.();
        });
    }
    private onChangeEntity(e: NSBoxCombo, callback?: () => void)
    {
        let entity = e.getValueOne(false);
        if (entity !== null)
            this.setState({ entity }, callback);
    }
    override render()
    {
        return (
            <AccessLayout<PolicyRow, PolicyInputRow> {...this.props}
                action={{
                    title: "",
                    description: "Create a new permission policy according to your requirements for precise control over account access.",
                    menus: {}
                }}
                pages={{
                    entity: AccessEntities.policy(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit:
                            {
                                editor: {
                                    getInputRow: () =>
                                    {
                                        let name: string = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let allow: boolean = this.NSBoxCombo_Effect.current?.getValue() === "Allow" ? true : false;
                                        let product_id: string = this.NSBoxEntity_Product.current?.getValueOne() ?? "";
                                        let entity: string = this.NSBoxCombo_Entity.current?.getValueOne() ?? "";
                                        let actions: string = this.NSBoxBooleans_Entity.current?.getValues().join() ?? "";
                                        let resources: string = this.NSRepeater_Resources.current?.getValue().join(",") ?? "";
                                        let description: string = this.NSBoxTextArea_Description.current?.getValue() ?? "";

                                        if (resources.length === 0)
                                            resources = "*";

                                        return { product_id, name, entity, actions, resources, allow, description, policy_category: [], policy_field: [], policy_tag: [] }
                                    },
                                    setRow: (row) =>
                                    {
                                        let actions: string[] = row.actions.split(",");
                                        let resources: string[] = [];
                                        if (row.resources !== "*")
                                            resources = row.resources.split(",");

                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxCombo_Effect.current?.setValue(row.allow ? "Allow" : "Deny");
                                        this.NSBoxEntity_Product.current?.setValue(row.product_id, () =>
                                        {
                                            this.NSBoxCombo_Entity.current?.setValue(row.entity, () =>
                                            {
                                                if (this.NSBoxCombo_Entity.current)
                                                    this.onChangeEntity(this.NSBoxCombo_Entity.current, () =>
                                                    {
                                                        this.NSBoxBooleans_Entity.current?.setValues(actions);
                                                        this.NSBoxTextArea_Description.current?.setValue(row.description ?? "");
                                                        this.NSRepeater_Resources.current?.setValue(resources);
                                                    });
                                            });
                                        });
                                    }
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_Name}
                            required
                            title="Name"
                            info={{ link: "https://namirasoft.com/access/console-guide/policy/#field-name" }}
                        />
                        <NSBoxCombo
                            ref={this.NSBoxCombo_Effect}
                            required
                            title="Effect"
                            multiple={false}
                            getOptions={() => Object.keys(PolicyEditPageEffect).map(key => ({
                                value: key,
                                title: key
                            }))}
                            defaultValue={PolicyEditPageEffect.Allow}
                            info={{ link: "https://namirasoft.com/access/console-guide/policy/#field-effect" }}
                        />
                    </NSRow>
                    <NSRow>
                        <NSBoxEntity
                            ref={this.NSBoxEntity_Product}
                            title="Product"
                            multiple={false}
                            required
                            list={async (filters, page, size, sorts, all_workspaces) =>
                            {
                                const res = await this.props.servers.access.product.List(filters, page, size, sorts, all_workspaces);
                                let rows = res.rows.filter(row => Object.keys(row.entities ?? {}).length > 0);
                                return {
                                    rows,
                                    count: rows.length
                                };
                            }}
                            table_name="product"
                            getValue={item => item.id}
                            getTitle={item => item.name}
                            getSort={() => null}
                            onChanged={this.onChangeProduct}
                            info={{ link: "https://namirasoft.com/access/console-guide/policy/#field-product" }}
                        />
                        <NSBoxCombo
                            ref={this.NSBoxCombo_Entity}
                            required
                            title="Entity"
                            multiple={false}
                            getOptions={() =>
                            {
                                return Object.keys(this.state.product?.entities ?? {}).map(key => ({
                                    value: key,
                                    title: NamingConvention.auto.convert(key, NamingConvention.Pascal_Case_Space)
                                }))
                            }}
                            onChanged={this.onChangeEntity}
                            info={{ link: "https://namirasoft.com/access/console-guide/policy/#field-entity" }}
                        />
                    </NSRow>
                    {
                        this.state.entity &&
                        <>
                            <NSTitle title="Actions" style={{ textAlign: 'start', color: '#141B5C' }} />
                            <NSRow style={{ maxWidth: NSBox.width.double }}							>
                                <NSBoxBooleans
                                    ref={this.NSBoxBooleans_Entity}
                                    box_all={{
                                        hideHeader: true
                                    }}
                                    boxes={{
                                        hideHeader: true
                                    }}
                                    items={
                                        this.state.product ?
                                            this.state.product?.entities[this.state.entity]?.map((item: string) =>
                                            {
                                                return {
                                                    value: item,
                                                    text: NamingConvention.auto.convert(item, NamingConvention.Pascal_Case_Space)
                                                }
                                            }) :
                                            [{
                                                value: "",
                                                text: ""
                                            }]
                                    }
                                />
                            </NSRow>
                        </>
                    }
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_Description}
                        required={false}
                        title="Description"
                        rows={5}
                        style={{ width: "100%", maxWidth: "unset" }}
                        info={{ link: "https://namirasoft.com/access/console-guide/policy/#field-description" }}
                    />
                    <NSLine />
                    <NSRepeater<ResourceLine, string>
                        ref={this.NSRepeater_Resources}
                        title="Resources"
                        required={false}
                        createItem={(gIndex: number, lIndex: number, ref: React.RefObject<ResourceLine>, onChange: () => void, onDelete: undefined | ((lindex: number) => void)) =>
                        {
                            return <ResourceLine
                                ref={ref}
                                key={`PolicyEditPage_NSRepeater_ResourceLine_key_${gIndex}`}
                                index={lIndex}
                                deletable={true}
                                onChanged={() => onChange()}
                                onDelete={() => onDelete?.(lIndex)}
                            />
                        }}
                        isEmpty={function (_, value: string | null): boolean
                        {
                            return ResourceLine.isEmpty(value)
                        }}
                        getValue={function (item: ResourceLine, checkError: boolean): string
                        {
                            return item.getValue(checkError);
                        }}
                        setValue={function (item: ResourceLine, _: number, value: string | null): void
                        {
                            if (value)
                                item.setValue(value)
                        }}
                        style={{ width: "100%" }}
                        info={{ link: "https://namirasoft.com/access/console-guide/policy/#field-resources" }}
                    />
                </NSColumn>
            </AccessLayout >
        );
    }
}